/**
 * Sangria/Suprimento Modal
 * Register cash withdrawal (sangria) or cash supply (suprimento).
 * Also shows movement history for the current shift.
 */
import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingDown, TrendingUp, History } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch } from '@/lib/api';

interface Movement {
  id: string;
  type: 'sangria' | 'suprimento';
  amount: number;
  reason: string;
  performed_at: string;
}

interface SangriaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function SangriaModal({ isOpen, onClose, onSuccess }: SangriaModalProps) {
  const [tab, setTab] = useState<'sangria' | 'suprimento' | 'historico'>('sangria');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [movements, setMovements] = useState<Movement[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  // Load movements when history tab is selected
  useEffect(() => {
    if (isOpen && tab === 'historico') {
      setLoadingMovements(true);
      apiFetch('/api/cash-register/movements')
        .then((res) => res.json())
        .then((data) => {
          if (data.movements) {
            setMovements(data.movements);
          }
        })
        .catch(() => toast.error('Erro ao carregar movimentações'))
        .finally(() => setLoadingMovements(false));
    }
  }, [isOpen, tab]);

  const resetForm = useCallback(() => {
    setAmount('');
    setReason('');
    setLoading(false);
    setTab('sangria');
    setMovements([]);
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [onClose, resetForm]);

  const handleSubmit = useCallback(async () => {
    const numAmount = parseFloat(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error('Informe um valor válido');
      return;
    }

    if (!reason.trim()) {
      toast.error('Informe o motivo');
      return;
    }

    setLoading(true);

    const endpoint = tab === 'sangria'
      ? '/api/cash-register/sangria'
      : '/api/cash-register/suprimento';

    try {
      const response = await apiFetch(endpoint, {
        method: 'POST',
        body: JSON.stringify({ amount: numAmount, reason }),
      });

      const data = await response.json();

      if (!response.ok) {
        toast.error(data.error || 'Erro ao registrar movimentação');
        setLoading(false);
        return;
      }

      const label = tab === 'sangria' ? 'Sangria' : 'Suprimento';
      toast.success(`${label} de R$${numAmount.toFixed(2)} registrada`);
      onSuccess();
      handleClose();
    } catch {
      toast.error('Erro de conexão');
      setLoading(false);
    }
  }, [amount, reason, tab, onSuccess, handleClose]);

  const formatCurrency = (v: number) =>
    v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <motion.div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
          />
          <motion.div
            className="relative z-10 w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-xl"
            initial={{ opacity: 0, scale: 0.95, y: 10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 10 }}
          >
            <button
              onClick={handleClose}
              className="absolute right-4 top-4 rounded-sm p-1 text-muted-foreground hover:text-foreground"
              aria-label="Fechar"
            >
              <X className="h-4 w-4" />
            </button>

            <h2 className="text-lg font-semibold">Movimentação de Caixa</h2>

            {/* Tabs */}
            <div className="mt-4 flex gap-1 rounded-lg border border-border/50 bg-muted/30 p-1">
              <button
                onClick={() => setTab('sangria')}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  tab === 'sangria'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <TrendingDown className="h-3.5 w-3.5" />
                Sangria
              </button>
              <button
                onClick={() => setTab('suprimento')}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  tab === 'suprimento'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <TrendingUp className="h-3.5 w-3.5" />
                Suprimento
              </button>
              <button
                onClick={() => setTab('historico')}
                className={`flex flex-1 items-center justify-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  tab === 'historico'
                    ? 'bg-card text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <History className="h-3.5 w-3.5" />
                Histórico
              </button>
            </div>

            {/* Sangria / Suprimento Form */}
            {(tab === 'sangria' || tab === 'suprimento') && (
              <div className="mt-4 space-y-4">
                <p className="text-sm text-muted-foreground">
                  {tab === 'sangria'
                    ? 'Retirada de dinheiro do caixa.'
                    : 'Adição de dinheiro ao caixa.'}
                </p>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Valor (R$) *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    placeholder="0.00"
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                    autoFocus
                  />
                </div>

                <div>
                  <label className="mb-1.5 block text-sm font-medium text-foreground">
                    Motivo *
                  </label>
                  <input
                    type="text"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                    placeholder={
                      tab === 'sangria'
                        ? 'Ex: Pagamento de fornecedor, troco...'
                        : 'Ex: Reforço de troco, depósito...'
                    }
                    className="w-full rounded-lg border border-border bg-background px-3 py-2.5 text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleClose}
                    className="flex-1 rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleSubmit}
                    disabled={loading || !amount || !reason.trim()}
                    className={`flex-1 rounded-lg px-4 py-2.5 text-sm font-medium text-white transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      tab === 'sangria'
                        ? 'bg-red-600 hover:bg-red-700'
                        : 'bg-green-600 hover:bg-green-700'
                    }`}
                  >
                    {loading
                      ? 'Registrando...'
                      : tab === 'sangria'
                        ? 'Registrar Sangria'
                        : 'Registrar Suprimento'}
                  </button>
                </div>
              </div>
            )}

            {/* History */}
            {tab === 'historico' && (
              <div className="mt-4">
                {loadingMovements ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Carregando...
                  </div>
                ) : movements.length === 0 ? (
                  <div className="py-8 text-center text-sm text-muted-foreground">
                    Nenhuma movimentação neste turno.
                  </div>
                ) : (
                  <div className="max-h-64 space-y-2 overflow-y-auto">
                    {movements.map((m) => (
                      <div
                        key={m.id}
                        className="flex items-center gap-3 rounded-lg border border-border/50 p-3"
                      >
                        {m.type === 'sangria' ? (
                          <TrendingDown className="h-4 w-4 shrink-0 text-red-400" />
                        ) : (
                          <TrendingUp className="h-4 w-4 shrink-0 text-green-400" />
                        )}
                        <div className="flex-1">
                          <div className="flex justify-between">
                            <span className="text-sm font-medium capitalize">{m.type}</span>
                            <span
                              className={`text-sm font-medium ${
                                m.type === 'sangria' ? 'text-red-400' : 'text-green-400'
                              }`}
                            >
                              {m.type === 'sangria' ? '-' : '+'}
                              {formatCurrency(Number(m.amount))}
                            </span>
                          </div>
                          <div className="flex justify-between text-xs text-muted-foreground">
                            <span>{m.reason}</span>
                            <span>
                              {new Date(m.performed_at).toLocaleTimeString('pt-BR', {
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
